import { narrationTimingGuidance } from 'js/src/agent/skills/assetTimingGuidance';
import { recordingCleanupGuidance } from 'js/src/agent/skills/recordingCleanupGuidance';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const talkingHeadVideoBasicsSkill: Skill = {
    name: SKILL_NAMES.talkingHeadVideoBasics,
    description:
        'The user has a video of someone speaking — an on-camera talking head or a narrated screen recording — to build the video around. Load BEFORE starting that build.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Talking Head Video Basics

The speaker is the point: they stay visible, and overlays are accents.

## Source video
- The talking head is always the user's own recording: a video attached to the chat
  or one of their uploads (load the
  \`${SKILL_NAMES.findUserMedia}\` skill to go through those). Never stock media as the talking
  head, and never generate narration. If there's no usable recording, ask the user
  to upload or record one.
- Call \`${TOOL_NAMES.addTalkingHeadSceneGroup}\` with the media_id — a single scene group for
  the whole recording; it transcribes the speech automatically.
- A full-screen person, a screen recording, or a screen recording with a small
  webcam bubble are all handled exactly the same way. Don't ask the user to choose
  between a "screen recording" and a "true talking head", don't tell them their
  video isn't a real talking head, and don't request a different version.
- This skill only covers a recording of someone on camera. Audio only (no video)
  is a recorded voiceover — use \`${TOOL_NAMES.addRecordedVoiceoverSceneGroup}\`; narration
  generated from a script is an AI voiceover. Neither needs this skill.

## Overlays or clean?
A talking head can be enriched with overlays or left clean. Decide before building,
with \`${TOOL_NAMES.askUserQuestion}\` — you must ASK unless the request is clearly one or the
other. "Make me a talking head video" is NOT clear. Explicit signals: "just the
talking head" / "no overlays" / "keep it clean" mean clean; "add lower thirds",
"cut in some b-roll", "make it dynamic" mean overlays. If the user has no
preference, the default is overlays. A clean talking head is essentially done once
the scene group exists — review and finish, and do NOT add overlays.

## Interspersed overlays — do NOT cover every word
The key difference from a voiceover video: the speaker is meant to be seen.
Overlays sit on top of the speaker at emphasis points, and you cut back to the
base talking-head scene between them. Uncovered stretches are correct and expected
— the speaker showing through is the desired look, not a gap to fill. Don't chase
coverage.
- Typical overlays: a lower third (the speaker's name/title or a key term), a
  callout (a point or stat as it's said), an occasional b-roll overlay of relevant
  media.
- Place them where they earn it: introducing the speaker or a new topic, a stat or
  key term worth reinforcing, something concrete that benefits from a visual.
- **Anchor each overlay that illustrates something on the words that actually
  mention it**, not on the start of the sentence building up to it. Text or media
  that appears before the speaker says the thing reads as dissonant — the viewer is
  shown something they haven't heard yet. When such an overlay needs more time on
  screen, extend it forward into the following speech rather than backwards into
  the run-up. An overlay that identifies rather than illustrates — a lower third
  naming the speaker — has nothing in the speech to anchor to and belongs over the
  opening words; don't drop it for lack of a mention.
- Density: interspersed, not continuous. Don't run overlay after overlay without
  returning to the speaker, and don't blanket the timeline with media — fewer,
  well-placed overlays beat wall-to-wall coverage.

## Adjusting or removing an overlay
Re-time an overlay with \`${TOOL_NAMES.setSceneRange}\` (change which transcript words it covers — grow, shrink,
or shift it) or drop it with \`${TOOL_NAMES.deleteScene}\`. Either just exposes the speaker underneath, which is
the intended look here — not a gap to re-cover.

${recordingCleanupGuidance()}

## Designs and text
- Favour lower thirds, callouts, and b-roll designs; match each overlay's design to
  its job, and vary the design from one overlay to the next.
- Overlay text stays short and punchy — a name, a title, a key phrase or stat —
  never the transcript.

${narrationTimingGuidance()}

## B-roll media
Load the \`${SKILL_NAMES.findStockMedia}\` skill before your first b-roll search. Each b-roll
overlay illustrates one specific moment — it should be immediately obvious what
it's reinforcing.

## Review
Load the \`${SKILL_NAMES.reviewVideo}\` skill before the review pass.
Additional checks:
- confirm that no overlay, callout, or text may cover the speaker's face
- confirm that the speaker stays well framed on all scenes
NOTE: Uncovered transcript ranges are NOT defects here.`;
    },
};
